import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { isPushSupported, getPushStatus, subscribePush, unsubscribePush } from "@/lib/push";
import Navbar from "@/components/Navbar";
import RideCard from "@/components/RideCard";
import { toast } from "sonner";
import { Save, RefreshCw, Bell, BellOff, CheckCircle2, XCircle, Camera, Trash2 } from "lucide-react";

export default function Profile() {
  const { user, updateProfile, uploadAvatar, removeAvatar, checkAuth } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [form, setForm] = useState({
    first_name: "", last_name: "", bio: "", twenty_min_power: 0, monthly_hours: 0, power_source: "manual",
  });
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [stravaConfigured, setStravaConfigured] = useState(false);
  const [myRides, setMyRides] = useState([]);
  const [pushStatus, setPushStatus] = useState({ supported: false, permission: "default", subscribed: false });
  const [pushBusy, setPushBusy] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [resettingPower, setResettingPower] = useState(false);
  const [komootForm, setKomootForm] = useState({ url: "", title: "", distance_km: "", elevation_m: "" });
  const [komootBusy, setKomootBusy] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user) {
      // Determine initial source for power: hidden if =0 and ever-saved hidden flag,
      // strava if connected & not manually overridden, else manual.
      let source = "manual";
      if (user.power_source) source = user.power_source;
      else if (user.power_manual_override) source = "manual";
      else if (user.strava_connected && (user.twenty_min_power || 0) > 0) source = "strava";
      setForm({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        bio: user.bio || "",
        twenty_min_power: user.twenty_min_power || 0,
        monthly_hours: user.monthly_hours || 0,
        power_source: source,
      });
    }
  }, [user]);

  useEffect(() => {
    api.get("/rides/user/me")
      .then((r) => setMyRides(r.data))
      .catch((err) => console.error("Profile my-rides fetch failed:", err));
    api.get("/strava/status")
      .then((r) => setStravaConfigured(!!r.data.configured))
      .catch(() => setStravaConfigured(false));
    if (isPushSupported()) {
      getPushStatus().then(setPushStatus).catch(() => {});
    }
  }, []);

  // Handle Strava callback returning ?strava=connected or ?strava_error=...
  useEffect(() => {
    const stravaState = searchParams.get("strava");
    const stravaErr = searchParams.get("strava_error");
    if (stravaState === "connected") {
      toast.success("Strava collegato! Sincronizza per aggiornare le statistiche.");
      checkAuth();
      setSearchParams({}, { replace: true });
    } else if (stravaErr) {
      toast.error(`Strava: ${stravaErr}`);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        first_name: form.first_name,
        last_name: form.last_name,
        bio: form.bio,
        monthly_hours: Number(form.monthly_hours) || 0,
        power_source: form.power_source,
      };
      // Only send twenty_min_power when source is manual (this also marks power_manual_override on backend).
      // When source is "strava", value is owned by strava sync. When "hidden", we send 0 explicitly.
      if (form.power_source === "manual") {
        payload.twenty_min_power = Number(form.twenty_min_power) || 0;
      } else if (form.power_source === "hidden") {
        payload.twenty_min_power = 0;
      }
      await updateProfile(payload);
      toast.success("Profilo aggiornato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const resetPowerToStrava = async () => {
    if (!window.confirm("Tornare al valore sincronizzato da Strava? La potenza manuale sarà sovrascritta al prossimo sync.")) return;
    setResettingPower(true);
    try {
      await api.post("/strava/power/reset");
      await checkAuth();
      toast.success("Override rimosso. Sincronizza ora per aggiornare il valore.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setResettingPower(false);
    }
  };

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushStatus.subscribed) {
        await unsubscribePush();
        toast.success("Notifiche push disattivate");
      } else {
        await subscribePush();
        toast.success("Notifiche push attive 🔔");
      }
      const next = await getPushStatus();
      setPushStatus(next);
    } catch (err) {
      toast.error(err.message || "Errore notifiche push");
    } finally {
      setPushBusy(false);
    }
  };

  const toggleConnect = async (key) => {
    try {
      const next = !user[key];
      await updateProfile({ [key]: next });
      toast.success(next ? "Account collegato (mock)" : "Account scollegato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const connectStrava = async () => {
    try {
      const { data } = await api.get("/strava/connect");
      window.location.href = data.auth_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore Strava");
    }
  };

  const syncStrava = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/strava/sync");
      toast.success(`Sincronizzato! FTP ${data.twenty_min_power}W · ${data.monthly_hours}h ultimi 30g`);
      await checkAuth();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore sincronizzazione");
    } finally {
      setSyncing(false);
    }
  };

  // Select "from Strava": clear manual override on backend, trigger a sync, then mark form source.
  const onSelectPowerStrava = async () => {
    if (!user.strava_connected) {
      toast.error("Collega prima Strava dalla sezione Integrazioni");
      return;
    }
    setSyncing(true);
    try {
      await api.post("/strava/power/reset");
      const { data } = await api.post("/strava/sync");
      await checkAuth();
      setForm((f) => ({ ...f, power_source: "strava", twenty_min_power: data.twenty_min_power || 0 }));
      toast.success(`Sincronizzato da Strava: ${data.twenty_min_power}W`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore sincronizzazione da Strava");
    } finally {
      setSyncing(false);
    }
  };

  const disconnectStrava = async () => {
    if (!window.confirm("Scollegare Strava?")) return;
    try {
      await api.post("/strava/disconnect");
      toast.success("Strava scollegato");
      await checkAuth();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const onAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 4MB)");
      return;
    }
    setAvatarBusy(true);
    try {
      await uploadAvatar(file);
      toast.success("Avatar aggiornato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore upload");
    } finally {
      setAvatarBusy(false);
    }
  };

  const onAvatarRemove = async () => {
    if (!user.picture) return;
    if (!window.confirm("Rimuovere l'avatar?")) return;
    setAvatarBusy(true);
    try {
      await removeAvatar();
      toast.success("Avatar rimosso");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAvatarBusy(false);
    }
  };

  const initials = `${(user?.first_name || "?")[0] || ""}${(user?.last_name || "")[0] || ""}`.toUpperCase();

  const parseKomoot = async () => {
    if (!komootForm.url) return;
    setKomootBusy(true);
    try {
      const { data } = await api.post("/komoot/parse", { url: komootForm.url });
      setKomootForm((prev) => ({
        ...prev,
        title: prev.title || data.title || "",
        distance_km: prev.distance_km || (data.distance_km ?? ""),
        elevation_m: prev.elevation_m || (data.elevation_m ?? ""),
      }));
      if (data.title) {
        toast.success("Dati estratti dalla pagina Komoot");
      } else {
        toast("Nessun dato auto-rilevato — compila a mano", { icon: "ℹ️" });
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore parsing");
    } finally {
      setKomootBusy(false);
    }
  };

  const addKomootTour = async () => {
    if (!komootForm.url || !komootForm.title) return;
    setKomootBusy(true);
    try {
      await api.post("/komoot/tours", {
        url: komootForm.url.trim(),
        title: komootForm.title.trim(),
        distance_km: komootForm.distance_km ? Number(komootForm.distance_km) : null,
        elevation_m: komootForm.elevation_m ? Number(komootForm.elevation_m) : null,
      });
      setKomootForm({ url: "", title: "", distance_km: "", elevation_m: "" });
      await checkAuth();
      toast.success("Tour Komoot salvato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore salvataggio");
    } finally {
      setKomootBusy(false);
    }
  };

  const removeKomootTour = async (tourId) => {
    if (!window.confirm("Rimuovere questo tour Komoot?")) return;
    try {
      await api.delete(`/komoot/tours/${tourId}`);
      await checkAuth();
      toast.success("Tour rimosso");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!user) return null;

  return (
    <>
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8 space-y-8">
        <div className="flex items-center gap-5">
          <div className="relative">
            <div
              className="w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden border-2 border-gray-200 bg-gray-100 flex items-center justify-center"
              data-testid="profile-avatar"
            >
              {user.picture ? (
                <img src={user.picture} alt="avatar" className="w-full h-full object-cover" />
              ) : (
                <span className="font-display font-black text-3xl text-gray-400 tracking-tighter">{initials}</span>
              )}
            </div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={avatarBusy}
              data-testid="avatar-upload-btn"
              className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-[#E11D48] text-white flex items-center justify-center hover:bg-[#BE123C] disabled:opacity-50 shadow"
              title="Cambia avatar"
            >
              <Camera className="w-4 h-4" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={onAvatarChange}
              className="hidden"
              data-testid="avatar-file-input"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-tag mb-2">Profilo</div>
            <h1 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tighter text-gray-900 truncate">
              {user.first_name} {user.last_name}
            </h1>
            <div className="text-gray-500 text-sm mt-1 font-mono truncate" data-testid="profile-email">{user.email}</div>
            {user.picture && (
              <button
                onClick={onAvatarRemove}
                disabled={avatarBusy}
                data-testid="avatar-remove-btn"
                className="mt-2 text-xs text-gray-500 hover:text-red-500 inline-flex items-center gap-1 uppercase tracking-wider"
              >
                <Trash2 className="w-3 h-3" /> Rimuovi avatar
              </button>
            )}
          </div>
        </div>

        {/* Edit form */}
        <form onSubmit={save} className="tactical-card rounded-sm p-6 space-y-5">
          <div>
            <div className="label-tag mb-1">Informazioni</div>
            <div className="font-display font-bold text-xl text-gray-900">Aggiorna i tuoi dati</div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label-tag block mb-2">Nome</label>
              <input data-testid="profile-first-name" value={form.first_name} onChange={update("first_name")} className="w-full bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-4 py-3 text-gray-900" />
            </div>
            <div>
              <label className="label-tag block mb-2">Cognome</label>
              <input data-testid="profile-last-name" value={form.last_name} onChange={update("last_name")} className="w-full bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-4 py-3 text-gray-900" />
            </div>
            <div>
              <label className="label-tag mb-2 block">20-min Power (W)</label>

              {/* Source selector: manual / strava / hidden */}
              <div className="inline-flex rounded-sm border border-gray-200 bg-gray-50 p-0.5 mb-2 flex-wrap" data-testid="power-source-selector">
                <button
                  type="button"
                  data-testid="power-source-manual"
                  onClick={() => setForm((f) => ({ ...f, power_source: "manual" }))}
                  className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold rounded-sm transition-colors ${
                    form.power_source === "manual"
                      ? "bg-[#E11D48] text-white"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Inserisci manuale
                </button>
                <button
                  type="button"
                  data-testid="power-source-strava"
                  onClick={() => onSelectPowerStrava()}
                  disabled={!user.strava_connected || syncing}
                  className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    form.power_source === "strava"
                      ? "bg-[#FC4C02] text-white"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                  title={!user.strava_connected ? "Collega prima Strava" : "Sincronizza la potenza dall'ultima attività Strava"}
                >
                  {syncing && form.power_source === "strava" ? "Sync..." : "Da Strava"}
                </button>
                <button
                  type="button"
                  data-testid="power-source-hidden"
                  onClick={() => setForm((f) => ({ ...f, power_source: "hidden", twenty_min_power: 0 }))}
                  className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold rounded-sm transition-colors ${
                    form.power_source === "hidden"
                      ? "bg-gray-700 text-white"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Nascondi
                </button>
              </div>

              {form.power_source === "hidden" ? (
                <div className="text-xs text-gray-500 italic px-1" data-testid="power-hidden-info">
                  La tua potenza non sarà mostrata sul profilo né su chi vuole unirsi ai giri.
                </div>
              ) : (
                <input
                  data-testid="profile-power"
                  type="number"
                  min="0"
                  max="600"
                  value={form.twenty_min_power}
                  onChange={update("twenty_min_power")}
                  disabled={form.power_source === "strava"}
                  className="w-full bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-4 py-3 text-gray-900 disabled:opacity-60 disabled:cursor-not-allowed"
                />
              )}
            </div>
            <div>
              <label className="label-tag block mb-2">Ore in sella / mese</label>
              <input data-testid="profile-hours" type="number" min="0" max="200" step="0.1" value={form.monthly_hours} onChange={update("monthly_hours")} className="w-full bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-4 py-3 text-gray-900" />
            </div>
          </div>
          <div>
            <label className="label-tag block mb-2">Bio</label>
            <textarea data-testid="profile-bio" rows={3} value={form.bio} onChange={update("bio")} placeholder="Raccontaci come pedali..." className="w-full bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-4 py-3 text-gray-900 resize-none" />
          </div>
          <button data-testid="profile-save-btn" type="submit" disabled={saving} className="btn-primary px-6 py-3 rounded-sm flex items-center gap-2 disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? "Salvataggio..." : "Salva modifiche"}
          </button>
        </form>

        {/* Notifiche Push */}
        <div className="tactical-card rounded-sm p-6" data-testid="push-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <div className="label-tag mb-1 flex items-center gap-2">
                <Bell className="w-3 h-3" /> Notifiche push
              </div>
              <div className="font-display font-bold text-xl text-gray-900 mb-1">
                Avvisami sul browser
              </div>
              <p className="text-xs text-gray-500 max-w-md">
                Ricevi una notifica push (anche a tab chiusa, finché il browser è aperto) quando un ciclista
                si unisce al tuo giro o ti scrive in chat. Funziona su desktop e Android.
              </p>
              {!pushStatus.supported && (
                <p className="text-[11px] text-yellow-500 mt-2 uppercase tracking-widest">
                  Browser non supportato (es. Safari iOS richiede l'app installata sulla home).
                </p>
              )}
              {pushStatus.permission === "denied" && (
                <p className="text-[11px] text-red-400 mt-2 uppercase tracking-widest">
                  Permesso bloccato. Sblocca dalle impostazioni del sito nel browser e riprova.
                </p>
              )}
            </div>
            <button
              data-testid="push-toggle-btn"
              onClick={togglePush}
              disabled={pushBusy || !pushStatus.supported || pushStatus.permission === "denied"}
              className={`px-5 py-3 rounded-sm font-bold uppercase tracking-wider text-xs flex items-center gap-2 transition-colors ${
                pushStatus.subscribed
                  ? "border border-gray-300 text-gray-900 hover:border-gray-400"
                  : "btn-primary"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {pushStatus.subscribed
                ? <><BellOff className="w-4 h-4" /> Disattiva</>
                : <><Bell className="w-4 h-4" /> {pushBusy ? "Attivazione..." : "Attiva push"}</>}
            </button>
          </div>
        </div>

        {/* Integrations */}
        <div className="tactical-card rounded-sm p-6">
          <div className="label-tag mb-1">Integrazioni</div>
          <div className="font-display font-bold text-xl text-gray-900 mb-1">Collega i tuoi account</div>
          <p className="text-xs text-gray-500 mb-5">
            Strava sincronizza FTP e ore in sella in automatico. Komoot per i tracciati dei tuoi giri.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {/* Strava — real OAuth */}
            <div className="border border-gray-200 rounded-sm p-4 flex flex-col" data-testid="integration-strava">
              <div className="flex items-center justify-between">
                <span className="font-display font-black text-2xl uppercase tracking-tighter" style={{ color: "#FC4C02" }}>Strava</span>
                {user.strava_connected ? <CheckCircle2 className="w-5 h-5 text-green-500" /> : <XCircle className="w-5 h-5 text-gray-400" />}
              </div>
              <p className="text-xs text-gray-500 mt-2 flex-1">
                {user.strava_connected ? "Sincronizza FTP e ore in sella in automatico." : "Collega per FTP e ore in sella reali."}
              </p>
              {!stravaConfigured && (
                <p className="text-[10px] text-yellow-500 mt-2 uppercase tracking-widest">Configurazione admin in corso</p>
              )}
              {user.strava_connected ? (
                <div className="mt-4 space-y-2">
                  <button data-testid="strava-sync-btn" onClick={syncStrava} disabled={syncing} className="w-full px-4 py-2 rounded-sm font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-2" style={{ background: "#FC4C02", color: "white" }}>
                    <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Sync..." : "Sincronizza"}
                  </button>
                  <button data-testid="strava-disconnect-btn" onClick={disconnectStrava} className="w-full px-4 py-2 rounded-sm font-bold uppercase tracking-wider text-xs border border-gray-300 text-gray-900 hover:border-gray-400">
                    Scollega
                  </button>
                </div>
              ) : (
                <button
                  data-testid="strava-connect-btn"
                  onClick={connectStrava}
                  disabled={!stravaConfigured}
                  className="mt-4 px-4 py-2 rounded-sm font-bold uppercase tracking-wider text-xs disabled:opacity-50"
                  style={{ background: "#FC4C02", color: "white", border: "none" }}
                >
                  Collega Strava
                </button>
              )}
            </div>

            {/* Komoot — saved tours */}
            <div className="border border-gray-200 rounded-sm p-4 flex flex-col md:col-span-2" data-testid="integration-komoot">
              <div className="flex items-center justify-between">
                <span className="font-display font-black text-2xl uppercase tracking-tighter" style={{ color: "#6AA127" }}>Komoot</span>
                {(user.komoot_tours || []).length > 0 ? <CheckCircle2 className="w-5 h-5 text-green-500" /> : <XCircle className="w-5 h-5 text-gray-400" />}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Salva qui i link dei tuoi tour Komoot. Quando pubblichi un giro li selezioni con un click invece di reincollarli ogni volta.
              </p>

              {/* Saved tours list */}
              {(user.komoot_tours || []).length > 0 && (
                <div className="mt-3 space-y-2" data-testid="komoot-tour-list">
                  {(user.komoot_tours || []).map((t) => (
                    <div key={t.tour_id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-sm border border-gray-200">
                      <a href={t.url} target="_blank" rel="noreferrer" className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-gray-900 truncate">{t.title}</div>
                        <div className="text-[11px] text-gray-500 font-mono">
                          {t.distance_km ? `${t.distance_km} km` : ""}{t.distance_km && t.elevation_m ? " · " : ""}{t.elevation_m ? `${t.elevation_m} m D+` : ""}
                          {!t.distance_km && !t.elevation_m && t.url}
                        </div>
                      </a>
                      <button
                        type="button"
                        onClick={() => removeKomootTour(t.tour_id)}
                        data-testid={`komoot-tour-remove-${t.tour_id}`}
                        className="text-gray-400 hover:text-red-500 p-1"
                        title="Rimuovi"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add new tour form */}
              <div className="mt-3 space-y-2 border-t border-gray-200 pt-3">
                <div className="flex gap-2">
                  <input
                    data-testid="komoot-new-url"
                    placeholder="https://www.komoot.com/tour/..."
                    value={komootForm.url}
                    onChange={(e) => setKomootForm({ ...komootForm, url: e.target.value })}
                    className="flex-1 bg-gray-50 border border-gray-200 focus:border-[#6AA127] focus:outline-none rounded-sm px-3 py-2 text-sm font-mono"
                  />
                  <button
                    type="button"
                    onClick={parseKomoot}
                    disabled={komootBusy || !komootForm.url}
                    data-testid="komoot-parse-btn"
                    className="px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-wider border border-gray-300 hover:border-gray-500 disabled:opacity-50"
                    title="Prova a leggere i dati dal link"
                  >
                    {komootBusy ? "..." : "Auto"}
                  </button>
                </div>
                <input
                  data-testid="komoot-new-title"
                  placeholder="Titolo del tour"
                  value={komootForm.title}
                  onChange={(e) => setKomootForm({ ...komootForm, title: e.target.value })}
                  className="w-full bg-gray-50 border border-gray-200 focus:border-[#6AA127] focus:outline-none rounded-sm px-3 py-2 text-sm"
                />
                <div className="flex gap-2">
                  <input
                    data-testid="komoot-new-km"
                    type="number"
                    step="0.1"
                    placeholder="km"
                    value={komootForm.distance_km}
                    onChange={(e) => setKomootForm({ ...komootForm, distance_km: e.target.value })}
                    className="flex-1 bg-gray-50 border border-gray-200 focus:border-[#6AA127] focus:outline-none rounded-sm px-3 py-2 text-sm"
                  />
                  <input
                    data-testid="komoot-new-elevation"
                    type="number"
                    placeholder="D+ m"
                    value={komootForm.elevation_m}
                    onChange={(e) => setKomootForm({ ...komootForm, elevation_m: e.target.value })}
                    className="flex-1 bg-gray-50 border border-gray-200 focus:border-[#6AA127] focus:outline-none rounded-sm px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    onClick={addKomootTour}
                    disabled={komootBusy || !komootForm.url || !komootForm.title}
                    data-testid="komoot-add-btn"
                    className="px-4 py-2 rounded-sm text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50"
                    style={{ background: "#6AA127" }}
                  >
                    Aggiungi
                  </button>
                </div>
                <p className="text-[10px] text-gray-400">
                  Suggerimento: apri il tuo tour su komoot.com, copia l'URL e incollalo qui. "Auto" prova a leggere titolo e dati dalla pagina pubblica.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* My rides */}
        <div>
          <div className="label-tag mb-2">I miei giri</div>
          <h2 className="font-display font-bold text-2xl text-gray-900 mb-4">Pubblicati & partecipati</h2>
          {myRides.length === 0 ? (
            <div className="tactical-card rounded-sm p-8 text-center text-gray-500 text-sm">
              Non hai ancora giri. Pubblica il primo!
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {myRides.map((r) => <RideCard key={r.ride_id} ride={r} />)}
            </div>
          )}
        </div>
      </main>
    </>
  );
}
